"use client"

import { useMemo, useState, useId, useSyncExternalStore } from "react"
import { ProgressPoint } from "@/services/analytics"
import { cn } from "@/lib/utils"
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Info, Check, Minus, X } from "lucide-react"
import { Skeleton } from "@/components/ui/skeleton"
import { format, subDays, eachDayOfInterval, isSameDay } from "date-fns"
import { surface, focusRing } from "@/lib/dashboard-ui"

interface ActivityHeatmapProps {
    data: ProgressPoint[]
    loading?: boolean
    onDayClick?: (date: Date) => void
}

type DayStatus = "met" | "partial" | "idle"

const TARGET_STORAGE_KEY = "heatmap_daily_target"
const DEFAULT_DAILY_TARGET = 5

/**
 * The saved daily goal lives in localStorage, which the server cannot read.
 * Seeding React state from it in a `useState` initializer (what this component
 * used to do) makes the first client render disagree with the server HTML and
 * throws a hydration error, and moving the read into an effect just trades that
 * for a cascading re-render. `useSyncExternalStore` is the sanctioned shape: the
 * server snapshot is the default, the client snapshot is the stored value.
 */
const targetListeners = new Set<() => void>()

function subscribeToTarget(onStoreChange: () => void) {
    targetListeners.add(onStoreChange)
    window.addEventListener("storage", onStoreChange)
    return () => {
        targetListeners.delete(onStoreChange)
        window.removeEventListener("storage", onStoreChange)
    }
}

function getStoredTarget(): number {
    const raw = localStorage.getItem(TARGET_STORAGE_KEY)
    const parsed = raw === null ? NaN : Number(raw)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_DAILY_TARGET
}

function getServerTarget(): number {
    return DEFAULT_DAILY_TARGET
}

function persistTarget(value: number) {
    localStorage.setItem(TARGET_STORAGE_KEY, String(value))
    targetListeners.forEach((listener) => listener())
}

/**
 * Status is a value, not a colour. Each day carries an icon and a word as well
 * as a fill, so the grid still reads for colour-blind users and in high
 * contrast — the previous version distinguished "missed" from "goal met" with
 * red vs. purple fills and nothing else.
 */
const STATUS: Record<DayStatus, { label: string; icon: typeof Check; fill: string; iconClass: string }> = {
    met: {
        label: "Goal met",
        icon: Check,
        fill: "bg-primary/25 border-primary/60",
        iconClass: "text-primary",
    },
    partial: {
        label: "Below goal",
        icon: Minus,
        fill: "bg-muted/70 border-border",
        iconClass: "text-muted-foreground",
    },
    idle: {
        label: "No activity",
        icon: X,
        fill: "bg-muted/40 border-border/60 border-dashed",
        iconClass: "text-muted-foreground/50",
    },
}

export function ActivityHeatmap({ data, loading, onDayClick }: ActivityHeatmapProps) {
    const targetInputId = useId()

    const storedTarget = useSyncExternalStore(subscribeToTarget, getStoredTarget, getServerTarget)

    // `draft` holds what is literally in the field while the student is typing,
    // so the input can be momentarily empty without wiping the saved goal.
    const [draft, setDraft] = useState<string | null>(null)
    const fieldValue = draft ?? String(storedTarget)

    const handleTargetChange = (raw: string) => {
        setDraft(raw)
        const parsed = Number(raw)
        if (raw !== "" && Number.isFinite(parsed) && parsed > 0) persistTarget(parsed)
    }

    const targetNum = (() => {
        const parsed = Number(fieldValue)
        return Number.isFinite(parsed) && parsed > 0 ? parsed : storedTarget
    })()

    const days = useMemo(() => {
        const end = new Date()
        return eachDayOfInterval({ start: subDays(end, 6), end })
    }, [])

    const statusFor = (count: number): DayStatus => {
        if (count === 0) return "idle"
        return count >= (targetNum || 1) ? "met" : "partial"
    }

    if (loading) {
        return (
            <div className={cn(surface, "p-5")}>
                <Skeleton className="mb-5 h-5 w-32" />
                <div className="grid grid-cols-7 gap-2">
                    {Array.from({ length: 7 }).map((_, i) => (
                        <Skeleton key={i} className="h-20 rounded-xl" />
                    ))}
                </div>
            </div>
        )
    }

    const weekTotal = days.reduce((sum, day) => {
        const point = data.find((p) => isSameDay(new Date(p.date), day))
        return sum + (point?.correctCount || 0)
    }, 0)
    const daysMet = days.filter((day) => {
        const point = data.find((p) => isSameDay(new Date(p.date), day))
        return statusFor(point?.correctCount || 0) === "met"
    }).length

    return (
        <TooltipProvider delayDuration={200}>
            <section aria-labelledby="activity-heading" className={cn(surface, "overflow-hidden p-5")}>
                <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                    <div className="min-w-0">
                        <h2 id="activity-heading" className="flex items-center gap-2 text-base font-bold tracking-tight text-foreground">
                            Activity
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        type="button"
                                        aria-label="What this shows"
                                        className={cn(
                                            // grid-2.2 target size: pad the 16px glyph out to 24x24.
                                            "-m-1 grid h-6 w-6 place-items-center rounded text-muted-foreground/70 transition-colors hover:text-foreground",
                                            focusRing
                                        )}
                                    >
                                        <Info className="h-4 w-4" aria-hidden="true" />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                    <p className="mb-1 font-semibold">Past 7 days</p>
                                    <p className="text-xs leading-relaxed">
                                        Questions answered correctly each day against your daily goal. Select a day to
                                        see everything you did on it.
                                    </p>
                                </TooltipContent>
                            </Tooltip>
                        </h2>
                        <p className="mt-0.5 text-sm text-muted-foreground">
                            <span className="tabular-nums">{weekTotal}</span> correct this week ·{" "}
                            <span className="tabular-nums">{daysMet}</span> of 7 days on goal
                        </p>
                    </div>

                    <div className="flex items-end gap-2">
                        <Label htmlFor={targetInputId} className="pb-2 text-sm font-medium text-muted-foreground">
                            Daily goal
                        </Label>
                        <Input
                            id={targetInputId}
                            type="number"
                            inputMode="numeric"
                            min={1}
                            value={fieldValue}
                            onChange={(e) => handleTargetChange(e.target.value)}
                            onBlur={() => setDraft(null)}
                            className={cn("h-11 w-20 rounded-xl text-center font-semibold tabular-nums", focusRing)}
                        />
                    </div>
                </div>

                <ul className="grid grid-cols-7 gap-2">
                    {days.map((day) => {
                        const dayData = data.find((p) => isSameDay(new Date(p.date), day))
                        const count = dayData?.correctCount || 0
                        const isToday = isSameDay(day, new Date())
                        const status = statusFor(count)
                        const { label, icon: StatusIcon, fill, iconClass } = STATUS[status]
                        const dateLabel = format(day, "EEEE, MMMM do")

                        return (
                            <li key={day.toISOString()}>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        {/* A real button: reachable by Tab, activated by Enter/Space,
                                            and named for screen readers. Was a bare <div onClick>. */}
                                        <button
                                            type="button"
                                            onClick={() => onDayClick?.(day)}
                                            aria-label={`${dateLabel}: ${count} correct, ${label.toLowerCase()}`}
                                            className={cn(
                                                "flex w-full flex-col items-center gap-1 rounded-xl border p-2 pb-2.5",
                                                "transition-[border-color,background-color,transform] duration-200",
                                                fill,
                                                focusRing,
                                                onDayClick ? "cursor-pointer motion-safe:hover:-translate-y-0.5" : "cursor-default",
                                                isToday && "ring-2 ring-primary ring-offset-2 ring-offset-background"
                                            )}
                                        >
                                            <StatusIcon className={cn("h-4 w-4", iconClass)} aria-hidden="true" />
                                            <span className="text-lg font-bold leading-none text-foreground tabular-nums">
                                                {count}
                                            </span>
                                            <span
                                                className={cn(
                                                    "text-[11px] font-semibold uppercase tracking-wide",
                                                    isToday ? "text-primary" : "text-muted-foreground"
                                                )}
                                            >
                                                {format(day, "eee")}
                                            </span>
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">
                                        <p className="font-semibold">
                                            {count} correct · {label}
                                        </p>
                                        <p className="text-xs opacity-80">{dateLabel}</p>
                                    </TooltipContent>
                                </Tooltip>
                            </li>
                        )
                    })}
                </ul>

                {/* Text legend — the fills alone do not carry the meaning. */}
                <ul className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-border pt-4">
                    {(Object.keys(STATUS) as DayStatus[]).map((key) => {
                        const { label, icon: StatusIcon, iconClass } = STATUS[key]
                        return (
                            <li key={key} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                <StatusIcon className={cn("h-3.5 w-3.5", iconClass)} aria-hidden="true" />
                                {label}
                            </li>
                        )
                    })}
                </ul>
            </section>
        </TooltipProvider>
    )
}
